import dayjs, { type ConfigType, type Dayjs } from 'dayjs';

/**
 * @description Helpers de fecha y hora basados en Day.js
 *
 * https://day.js.org/
 */
const DateTimeUtils = () => {};

interface DateInput {
  date: ConfigType;
  format?: string;
  utc?: boolean;
}

// @TODO: agregar en el .env y sacarlo de aca
export const DEFAULT_FORMAT_DATE = 'DD-MM-YYYY';
export const DEFAULT_FORMAT_DATE_2 = 'DD/MM/YYYY';
export const ALTERNATIVE_FORMAT_DATE = 'DDMMYYYY';
export const FORMAT_DATE_TIME_1 = 'DD-MM-YYYY HH:mm';
export const FORMAT_DATE_TIME_2 = 'YYYY-MM-DD HH:mm:ss';
export const FORMAT_DATE_TIME_3 = 'YYYY-MM-DD';
export const FORMAT_DATE_TIME_4 = 'DD/MM/YYYY';
export const FORMAT_DATE_TIME_5 = 'DD/MM/YYYY HH:mm';
export const FORMAT_DATE_TIME_6 = 'DD/MM/YYYY HH:mm:ss';
export const FORMAT_DATE_TIME_7 = 'DD-MM-YYYY HH:mm:ss:SS';

export const DEFAULT_TIMEZONE = 'America/Buenos_Aires';

/**
 * Devuelve la fecha y hora actual
 * @param {String} set_format
 * @returns {String}
 */
const getEventDayjs = (event: DateInput): Dayjs | null => {
  if (event.date) {
    const numericDate = Number(event.date);
    const isNumericDate = !Number.isNaN(numericDate);
    const datetime = isNumericDate ? numericDate : event.date;
    if (isNumericDate) {
      return event.utc ? dayjs.utc(datetime) : dayjs(datetime);
    }
    return event.utc
      ? dayjs.utc(datetime, event.format || DEFAULT_FORMAT_DATE)
      : dayjs(datetime, event.format || DEFAULT_FORMAT_DATE);
  }
  return null;
};

/**
 * Devuelve la fecha y hora actual
 * @param {String} set_format
 * @returns {String}
 */
DateTimeUtils.getMoment = (event?: DateInput): Dayjs => {
  return event ? (getEventDayjs(event) ?? dayjs()) : dayjs();
};

/**
 * Devuelve la fecha en el formato por default
 * @param {Dayjs} event_moment
 * @returns {String}
 */
DateTimeUtils.getDefaultFormat = (event_moment: Dayjs) => {
  return event_moment.format(DEFAULT_FORMAT_DATE);
};

/**
 * Devuelve la fecha y hora actual
 * @param {String} set_format
 * @returns {String}
 */
DateTimeUtils.currentMoment = (set_format?: string) => {
  return set_format ? dayjs().format(set_format) : dayjs().format();
};

/**
 * Devuelve la fecha y hora especificada
 * @param {String} event.date
 * @param {String} set_format
 * @returns {String}
 */
DateTimeUtils.specificMoment = (event: DateInput, set_format?: string) => {
  if (set_format) {
    return getEventDayjs(event)?.format(set_format);
  }
  return getEventDayjs(event)?.format();
};

/**
 * Devuelve la fecha y hora en el formato por default
 * @returns {String}
 */
DateTimeUtils.momentDefaultFormat = (event?: DateInput) => {
  if (event) {
    return getEventDayjs(event)?.format(DEFAULT_FORMAT_DATE);
  }
  return dayjs().format(DEFAULT_FORMAT_DATE);
};

/**
 * Devuelve la fecha y hora en el formato alternativo
 * @returns {String}
 */
DateTimeUtils.momentAlternativeFormat = (event?: DateInput) => {
  if (event) {
    return getEventDayjs(event)?.format(ALTERNATIVE_FORMAT_DATE);
  }
  return dayjs().format(ALTERNATIVE_FORMAT_DATE);
};

/**
 * Devuelve la fecha y hora en formato ISO
 * @returns {String}
 */
DateTimeUtils.momentToISOString = (event?: DateInput) => {
  return event ? getEventDayjs(event)?.toISOString() : dayjs().toISOString();
};

/**
 * Devuelve la fecha y hora en formato unix
 * @returns {String}
 */
DateTimeUtils.momentUnix = (event?: DateInput) => {
  if (event) {
    const res = getEventDayjs({ ...event, utc: true });
    return res ? res.unix() * 1000 : 0;
  }
  return dayjs().unix() * 1000;
};

/**
 * Devuelve la fecha y hora actual en UTC (Coordinated Universal Time)
 * Ej: 2020-07-21T09:29:34-03:00 -> 2020-07-21T12:29:34Z
 * @param {String} event_format
 * @returns {String}
 */
DateTimeUtils.getMomentUTC = (event?: DateInput, set_format?: string) => {
  const momUnix = DateTimeUtils.momentUnix(event);
  if (set_format) {
    return momUnix
      ? dayjs.utc(momUnix).format(set_format)
      : dayjs.utc().format(set_format);
  }
  return momUnix ? dayjs.utc(momUnix).toISOString() : dayjs.utc().toISOString();
};

DateTimeUtils.getDateRangeToUnixCalendar = (dates: [Dayjs, Dayjs]) => {
  const init = DateTimeUtils.getDefaultFormat(dates[0]);
  const end = DateTimeUtils.getDefaultFormat(dates[1]);
  const initUnix = DateTimeUtils.momentUnix({ date: init });
  const endUnix = DateTimeUtils.momentUnix({
    date: `${end} 23:59:59`,
    format: 'DD-MM-YYYY HH:mm:ss',
  });
  return [initUnix, endUnix];
};

export { DateTimeUtils };
